import { createContext, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * TabGroup から配下の TabButton へ「スライドピル方式かどうか」を伝えるコンテキスト。
 * sliding が true のとき、TabButton は自身のアクティブ背景を描画せず、
 * TabGroup が描画する背後のスライドピルを見せる。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business TabGroup→TabButton 間でスライドピル方式か否かを伝える React Context。TabButton がアクティブ背景を自前描画するかの分岐に使う
 * @graph-connects none
 */
export const TabGroupContext = createContext<{ sliding: boolean; shape: TabGroupShape }>({
  sliding: false,
  shape: 'rounded',
});

/**
 * スイッチの角の形。
 *  - `rounded`（既定）: 角丸長方形。アプリ全体の既存スイッチの見た目
 *  - `pill`: 完全な丸（カプセル型）。チャートカードのヘッダーに置く小さめの切替で使う
 * @graph-connects none
 */
export type TabGroupShape = 'rounded' | 'pill';

interface TabGroupProps {
  /** タブ群（通常 <TabButton> 複数） */
  children: ReactNode;
  /** スクリーンリーダー向けラベル */
  ariaLabel?: string;
  /** 追加クラス */
  className?: string;
  /**
   * アクティブタブの背後に「スライドするピル」を表示し、タブ切替時に水平移動アニメーションさせる。
   * **既定で有効**。プロジェクト全体のスイッチUIはこのスライド方式に統一する方針のため、
   * 無効化したい特別なケースでのみ `sliding={false}` を指定すること。
   */
  sliding?: boolean;
  /**
   * 角の形。既定は `rounded`（既存スイッチの見た目を変えない）。
   * チャートカードのヘッダーに置く切替は `pill` で統一する。
   */
  shape?: TabGroupShape;
}

/** スライドピルの位置・サイズ */
interface PillRect {
  left: number;
  width: number;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business タブ風セグメントボタンのコンテナ。<TabButton>群をラップして角丸の枠と背景を提供する。既定でアクティブタブ背後にスライドするピルを描画し、タブ切替時に水平移動アニメーションする。shape='pill' で完全な丸（カプセル型）にできる（チャートカードのヘッダー用）
 * @graph-connects none
 */
export function TabGroup({
  children,
  ariaLabel,
  className = '',
  sliding = true,
  shape = 'rounded',
}: TabGroupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<PillRect | null>(null);

  // アクティブタブ（aria-selected="true"）の位置を測ってピルを移動させる。
  // 毎レンダー後に再測定（タブ切替で親が再レンダーされる）+ ResizeObserver でレイアウト変化に追従。
  useLayoutEffect(() => {
    if (!sliding) return;
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const active = container.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (!active) {
        // 同一参照を返して再レンダーをスキップ（毎レンダー effect でのループ防止）
        setPill((prev) => (prev === null ? prev : null));
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const next: PillRect = {
        left: activeRect.left - containerRect.left,
        width: activeRect.width,
      };
      // 値が変わらない場合は前の state をそのまま返し、無限再レンダーを防ぐ
      setPill((prev) =>
        prev && prev.left === next.left && prev.width === next.width ? prev : next,
      );
    };

    measure();
    // ResizeObserver 未対応環境（jsdom 等）では初回測定のみで graceful degrade する
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  });

  // consumer (TabButton) の不要な context 駆動再レンダーを避けるため value を memoize する
  const ctxValue = useMemo(() => ({ sliding, shape }), [sliding, shape]);
  const isPill = shape === 'pill';

  return (
    <TabGroupContext.Provider value={ctxValue}>
      <div
        ref={containerRef}
        role="tablist"
        aria-label={ariaLabel}
        className={`relative inline-flex bg-[var(--color-bg-elevated)] p-1 gap-1 ${
          isPill ? 'rounded-full' : 'rounded-lg'
        } ${className}`}
      >
        {sliding && pill && (
          <span
            aria-hidden="true"
            className={`absolute top-1 bottom-1 bg-[var(--color-bg-surface)] shadow-sm transition-[left,width] duration-300 ease-out ${
              isPill ? 'rounded-full' : 'rounded-md'
            }`}
            style={{ left: pill.left, width: pill.width }}
          />
        )}
        {children}
      </div>
    </TabGroupContext.Provider>
  );
}
